// 共有データベース（MariaDB・Redis の専用 VM）。サーバーをまたいでお金などをそろえるプラグイン向け。
import { useState, type FormEvent } from 'react';
import { SharedDbNameSchema, type SharedDatabaseView, type SharedDbCredentials } from '@mcpanel/core';
import { api } from '../api/client';
import { sharedDbApi } from '../api/shared-db';
import { errorMessage, useApp } from '../app-context';
import { LoadingContent, Spinner } from '../components/feedback';
import { Badge, Empty, ErrorAlert } from '../components/ui';
import { FieldError } from '../components/FieldError';
import { formatBytes, formatDateTime } from '../format';
import { usePoll } from '../hooks/usePoll';

export function SharedDatabaseSection() {
  const { me, canOperate } = useApp();
  const owner = me?.user?.role === 'owner';
  const view = usePoll(() => (canOperate ? sharedDbApi.view() : Promise.resolve(undefined)), 5000, [canOperate]);
  if (!canOperate) return null;
  return (
    <section className="card stack">
      <h2 data-search-id="shared-database">共有データベース</h2>
      <p className="small muted">
        サーバーをまたいでデータをそろえるための MariaDB と Redis です（例: The New Economy で survival と hub
        のお金を同じにする）。専用の小さな VM を 1 台作り、ここで選んだサーバーの VM からだけつなげます。
        インターネットには出しません。毎日 4 時に中身を書き出して、7 日分残します。
      </p>
      <LoadingContent loading={!view.data && view.loading} shape="settings">
        <ErrorAlert error={view.error} />
        {view.data &&
          (view.data.host ? (
            <HostPanel data={view.data} owner={owner} reload={view.reload} />
          ) : (
            <CreateHost data={view.data} owner={owner} reload={view.reload} />
          ))}
      </LoadingContent>
    </section>
  );
}

interface PanelProps {
  data: SharedDatabaseView;
  owner: boolean;
  reload: () => Promise<unknown>;
}

function useAction(reload: () => Promise<unknown>) {
  const { notify } = useApp();
  const [busy, setBusy] = useState(false);
  const run = async (operation: () => Promise<unknown>, message?: string) => {
    setBusy(true);
    try {
      await operation();
      await reload();
      if (message) notify(message);
      return true;
    } catch (error) {
      notify(errorMessage(error), true);
      return false;
    } finally {
      setBusy(false);
    }
  };
  return { busy, run };
}

function CreateHost({ data, owner, reload }: PanelProps) {
  const { busy, run } = useAction(reload);
  const preferred = data.nodes.find((n) => n.role === 'infra') ?? data.nodes[0];
  const [node, setNode] = useState(preferred?.name ?? '');
  const [memoryMb, setMemoryMb] = useState(2048);
  if (!owner) return <Empty>まだ作られていません。作れるのはオーナーだけです。</Empty>;
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    await run(() => sharedDbApi.createHost({ node, memoryMb }), '共有データベースの VM を作り始めました');
  };
  return (
    <form className="stack" onSubmit={(event) => void submit(event)}>
      <div className="row">
        <label className="stack" style={{ flex: '1 1 220px' }}>
          <span className="small">置くノード</span>
          <select className="input" value={node} aria-label="置くノード" onChange={(e) => setNode(e.target.value)}>
            {data.nodes.map((n) => (
              <option key={n.name} value={n.name}>
                {n.name}（{n.role}・空き {Math.floor(n.memoryFreeMb / 1024)}GB）
              </option>
            ))}
          </select>
        </label>
        <label className="stack" style={{ flex: '1 1 160px' }}>
          <span className="small">メモリ</span>
          <select
            className="input"
            value={memoryMb}
            aria-label="メモリ"
            onChange={(e) => setMemoryMb(Number(e.target.value))}
          >
            {[1024, 2048, 4096].map((mb) => (
              <option key={mb} value={mb}>
                {mb / 1024}GB
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="small muted">CPU 2 コア・ディスク 20GB・固定 IP で作ります。数分かかります。</p>
      <div>
        <button className="btn primary" disabled={busy || !node}>
          {busy && <Spinner />}
          共有データベースを作る
        </button>
      </div>
    </form>
  );
}

const stateLabel = { provisioning: '作成中', ready: '動いています', error: '作成に失敗しました' } as const;

function HostPanel({ data, owner, reload }: PanelProps) {
  const host = data.host!;
  const { busy, run } = useAction(reload);
  const status = host.status;
  const tone =
    host.state === 'error' ? 'danger' : host.state === 'provisioning' ? 'info' : host.connected ? 'ok' : 'warn';
  return (
    <div className="stack">
      <div className="row" style={{ alignItems: 'center' }}>
        <Badge tone={tone}>{host.state === 'ready' && !host.connected ? 'agent 未接続' : stateLabel[host.state]}</Badge>
        <span className="small">
          {host.node} ・ VM {host.vmid ?? '—'} ・ <code>{host.address ?? 'IP 未定'}</code> ・ メモリ{' '}
          {host.memoryMb / 1024}GB
        </span>
      </div>
      {host.message && <p className="alert danger small">{host.message}</p>}
      {host.state !== 'ready' && (
        <ol className="small muted">
          {host.steps.map((step, index) => (
            <li key={index}>{step}</li>
          ))}
        </ol>
      )}
      {host.state === 'error' && owner && (
        <div className="row">
          <button
            className="btn"
            disabled={busy}
            onClick={() =>
              void run(() => sharedDbApi.removeFailedHost(), '失敗した VM を片付けました。もう一度作れます')
            }
          >
            片付けて作り直す
          </button>
        </div>
      )}
      {host.state === 'ready' && (
        <>
          {status && (
            <p className="small">
              MariaDB {status.mariadb.active ? '○' : '×'} ・ Redis {status.redis.active ? '○' : '×'}
              {status.disk && ` ・ ディスクの空き ${formatBytes(status.disk.freeBytes)}`}
              {status.appliedAt && ` ・ 最後の反映 ${formatDateTime(status.appliedAt)}`}
            </p>
          )}
          {status?.error && <p className="alert danger small">{status.error}</p>}
          <DatabaseList data={data} owner={owner} reload={reload} />
          <Backups data={data} owner={owner} reload={reload} busy={busy} run={run} />
        </>
      )}
    </div>
  );
}

function DatabaseList({ data, owner, reload }: PanelProps) {
  const { busy, run } = useAction(reload);
  const servers = usePoll(() => api.servers(), 60000, []);
  const [name, setName] = useState('');
  const [shown, setShown] = useState<SharedDbCredentials>();
  const parsed = SharedDbNameSchema.safeParse(name);
  const ids = (servers.data ?? []).map((s) => s.id);
  const sizes = new Map(data.host?.status?.databases.map((d) => [d.name, d.sizeBytes]) ?? []);
  const toggle = (dbName: string, current: string[], serverId: string) =>
    run(
      () =>
        sharedDbApi.setDatabase(
          dbName,
          current.includes(serverId) ? current.filter((id) => id !== serverId) : [...current, serverId],
        ),
      `${dbName} につなげるサーバーを変えました`,
    );
  const add = async (event: FormEvent) => {
    event.preventDefault();
    if (!parsed.success) return;
    if (await run(() => sharedDbApi.setDatabase(parsed.data, []), `${parsed.data} を作りました`)) setName('');
  };
  return (
    <div className="stack">
      <h3>データベース</h3>
      {data.databases.length === 0 && <Empty>データベースはまだありません。</Empty>}
      {data.databases.map((db) => (
        <div key={db.name} className="card stack">
          <div className="row" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <strong>
              <code>{db.name}</code>
            </strong>
            <span className="small muted">{sizes.has(db.name) ? formatBytes(sizes.get(db.name)!) : '—'}</span>
          </div>
          <div className="row small" role="group" aria-label={`${db.name} につなげるサーバー`}>
            {ids.map((serverId) => (
              <label key={serverId} className="row" style={{ gap: 4 }}>
                <input
                  type="checkbox"
                  checked={db.servers.includes(serverId)}
                  disabled={!owner || busy}
                  onChange={() => void toggle(db.name, db.servers, serverId)}
                />
                {serverId}
              </label>
            ))}
          </div>
          {db.unreachable.length > 0 && (
            <p className="alert danger small">IP が分からずつなげないサーバー: {db.unreachable.join('、')}</p>
          )}
          {owner && (
            <div className="row">
              <button
                className="btn small"
                disabled={busy}
                onClick={() => void run(async () => setShown(await sharedDbApi.credentials(db.name)))}
              >
                接続情報を見る
              </button>
              <button
                className="btn small danger"
                disabled={busy}
                onClick={() => {
                  if (!window.confirm(`${db.name} のユーザーを消します（中のデータは残ります）。よいですか？`)) return;
                  void run(() => sharedDbApi.deleteDatabase(db.name), `${db.name} を消しました`);
                }}
              >
                消す
              </button>
            </div>
          )}
          {shown?.name === db.name && <CredentialsView credentials={shown} onClose={() => setShown(undefined)} />}
        </div>
      ))}
      {owner && (
        <form className="row" onSubmit={(event) => void add(event)}>
          <input
            className="input"
            style={{ flex: '1 1 220px' }}
            value={name}
            placeholder="名前（例: economy）"
            aria-label="データベースの名前"
            onChange={(event) => setName(event.target.value)}
          />
          <button className="btn" disabled={busy || !parsed.success}>
            {busy && <Spinner />}
            データベースを作る
          </button>
        </form>
      )}
      {name && !parsed.success && (
        <FieldError id="shared-db-name-error" messages={[parsed.error.issues[0]?.message ?? '']} />
      )}
    </div>
  );
}

function CredentialsView({ credentials, onClose }: { credentials: SharedDbCredentials; onClose: () => void }) {
  const rows: Array<[string, string]> = [
    ['ホスト', credentials.host ?? '—'],
    ['MariaDB のポート', String(credentials.mariadb.port)],
    ['データベース名', credentials.mariadb.database],
    ['MariaDB のユーザー', credentials.mariadb.user],
    ['MariaDB のパスワード', credentials.mariadb.password],
    ['Redis のポート', String(credentials.redis.port)],
    ['Redis のユーザー', credentials.redis.user],
    ['Redis のパスワード', credentials.redis.password],
  ];
  return (
    <div className="stack" aria-label={`${credentials.name} の接続情報`}>
      <p className="small muted">プラグインの設定ファイルに入れてください。人に見せないでください。</p>
      <div className="table-wrap">
        <table>
          <tbody>
            {rows.map(([label, value]) => (
              <tr key={label}>
                <th>{label}</th>
                <td>
                  <code>{value}</code>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <button className="btn small" onClick={() => void navigator.clipboard?.writeText(value)}>
                    コピー
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div>
        <button className="btn small" onClick={onClose}>
          閉じる
        </button>
      </div>
    </div>
  );
}

function Backups({
  data,
  owner,
  busy,
  run,
}: PanelProps & { busy: boolean; run: (operation: () => Promise<unknown>, message?: string) => Promise<boolean> }) {
  const backups = data.host?.status?.backups ?? [];
  return (
    <div className="stack">
      <h3>バックアップ</h3>
      <p className="small muted">
        VM の <code>/data/backups</code> に毎日 4 時に書き出し、新しい 7 日分を残します。VM
        のスナップショットとは別です。
      </p>
      {backups.length === 0 && <Empty>まだありません。</Empty>}
      {backups.length > 0 && (
        <ul className="small">
          {backups.map((backup) => (
            <li key={backup.file}>
              <code>{backup.file}</code>（{formatBytes(backup.sizeBytes)}）
            </li>
          ))}
        </ul>
      )}
      {owner && (
        <div>
          <button
            className="btn small"
            disabled={busy || !data.host?.connected}
            onClick={() => void run(() => sharedDbApi.backup(), 'バックアップを取りました')}
          >
            {busy && <Spinner />}
            今すぐバックアップ
          </button>
        </div>
      )}
    </div>
  );
}
